import { zodResolver } from '@hookform/resolvers/zod';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { VirtualVehicleVehicle } from '@/@types/VirtualVehicle';
import { useNavigate } from '@/providers/RouterShim';
import useBranch from '../../../../data/branch/useBranch';
import useCreateVirtualAd from '../../../../data/virtual-vehicles/useCreateVirtualAd';
import AddButton from '../../../../styled-components/components/AddButton';
import { Box } from '../../../../styled-components/components/Box';
import BulletSeparatedList from '../../../../styled-components/components/BulletSeparatedList';
import { Button } from '../../../../styled-components/components/Button';
import { Column, Columns } from '../../../../styled-components/components/Columns';
import Content from '../../../../styled-components/components/Content';
import { Form, FormGroup } from '../../../../styled-components/components/Form';
import { H5 } from '../../../../styled-components/components/Heading';
import InputSelect from '../../../../styled-components/components/InputSelect';
import InputText from '../../../../styled-components/components/InputText';
import { ShallowPanelBody } from '../../../../styled-components/components/Panel/ShallowPanel';
import { StackLarge } from '../../../../styled-components/components/Stack';
import { Text } from '../../../../styled-components/components/Text';
import { VStack } from '../../../../styled-components/components/VStack';
import AddFromTemplate from './AddFromTemplate';
import { CreateVehicleFooter } from './Footer';
import { deliveryTime, deliveryUnit, virtualVehicleId } from './validationMethods';

const vehicleFormSchema = z.object({
  virtualVehicleId,
  deliveryTime,
  deliveryUnit,
});

type VehicleFormSchema = z.infer<typeof vehicleFormSchema>;

interface CreateOrderVehicleProps {
  onClose: () => void;
  showTemplateSelect: boolean;
  setShowTemplateSelect: (showTemplateSelect: boolean) => void;
}

const CreateOrderVehicle = ({
  onClose,
  showTemplateSelect,
  setShowTemplateSelect,
}: CreateOrderVehicleProps) => {
  const { t } = useTranslation(['common', 'createVehiclePanel', 'glossary']);
  const [virtualVehicle, setVirtualVehicle] = useState<VirtualVehicleVehicle>();
  const [createVirtual, createVirtualRequest, createVirtualResponse] = useCreateVirtualAd();
  const navigate = useNavigate();
  const [branch] = useBranch();

  const {
    register,
    formState: { errors },
    watch,
    setValue,
    handleSubmit,
    resetField,
  } = useForm<VehicleFormSchema>({
    mode: 'onSubmit',
    resolver: zodResolver(vehicleFormSchema),
    defaultValues: {
      deliveryUnit: 'month',
    },
  });

  const onCreateVehicle = handleSubmit((data) => {
    if (virtualVehicle?.id === undefined) return;

    createVirtual({
      vehicleId: virtualVehicle?.id,
      expectedDelivery: {
        from: data.deliveryTime.from,
        to: data.deliveryTime.to,
        unit: data.deliveryUnit,
      },
    });
  });

  useEffect(() => {
    if (createVirtualRequest.isSuccess) {
      navigate(`/${branch?.id}/vehicles/administrate-virtual-vehicles/${createVirtualResponse}`);
      onClose();
    }
  }, [createVirtualRequest.isSuccess]);

  if (showTemplateSelect) {
    const deliveryTime = watch('deliveryTime');
    return (
      <AddFromTemplate
        resetForm={() => {
          if (!deliveryTime.from) {
            resetField('deliveryTime.from');
          }
          if (!deliveryTime.to) {
            resetField('deliveryTime.to');
          }
        }}
        onClose={() => {
          setShowTemplateSelect(false);
        }}
        onSelect={(virtualVehicle) => {
          setValue('virtualVehicleId', virtualVehicle.id);
          setVirtualVehicle(virtualVehicle);
        }}
      />
    );
  }

  return (
    <>
      <ShallowPanelBody>
        <StackLarge>
          <Content>
            <p>{t('orderVehicleDescription', { ns: 'createVehiclePanel' })}</p>
          </Content>
        </StackLarge>
        <StackLarge>
          {!virtualVehicle ? (
            <AddButton
              onClick={() => setShowTemplateSelect(true)}
              title={t('selectTemplate', { ns: 'createVehiclePanel' })}
              inputHeight
            >
              {t('selectTemplate', { ns: 'createVehiclePanel' })}
            </AddButton>
          ) : (
            <Box>
              <Columns $spacing={2} $valign="center">
                <Column $grow>
                  <VStack $spacing={0.5}>
                    <Text $size="small">
                      <Text as="span" $weight="bold">
                        {virtualVehicle.manufacturer}
                      </Text>{' '}
                      {virtualVehicle.salesName}
                    </Text>
                    <BulletSeparatedList
                      size="small"
                      tone="alt"
                      items={[
                        virtualVehicle.modelYear,
                        virtualVehicle.drivingWheel,
                        virtualVehicle.batteryCapacity
                          ? `${virtualVehicle.batteryCapacity}kWh`
                          : undefined,
                      ]}
                    />
                  </VStack>
                </Column>
                <Column $noShrink>
                  <Button
                    $tone="secondary"
                    $size="small"
                    onClick={() => setShowTemplateSelect(true)}
                    title={t('changeButton', { ns: 'createVehiclePanel' })}
                  >
                    {t('changeButton', { ns: 'createVehiclePanel' })}
                  </Button>
                </Column>
              </Columns>
            </Box>
          )}
        </StackLarge>
        <StackLarge>
          <H5 as="h3" $margin="small">
            {t('deliveryTimeHeading', { ns: 'createVehiclePanel' })}
          </H5>
          <Form style={{ '--min-width': '100px' } as React.CSSProperties}>
            <FormGroup $width="half">
              <InputText
                label={t('from', { ns: 'glossary' })}
                type="number"
                register={register('deliveryTime.from', { valueAsNumber: true })}
                error={t(errors.deliveryTime?.from?.message ?? '', { ns: 'createVehiclePanel' })}
              />
            </FormGroup>
            <FormGroup $width="half">
              <InputText
                label={t('to', { ns: 'glossary' })}
                type="number"
                register={register('deliveryTime.to', { valueAsNumber: true })}
                error={t(
                  (errors.deliveryTime?.to?.message || errors.deliveryTime?.root?.message) ?? '',
                  { ns: 'createVehiclePanel' },
                )}
              />
            </FormGroup>
            <FormGroup>
              <InputSelect
                label={t('unit', { ns: 'glossary' })}
                title={t('unit', { ns: 'glossary' })}
                options={[
                  {
                    value: 'week',
                    displayName: t('weeks', { ns: 'glossary' }),
                  },
                  {
                    value: 'month',
                    displayName: t('months', { ns: 'glossary' }),
                  },
                ]}
                defaultValue="days"
                register={register('deliveryUnit')}
                error={t(errors.deliveryUnit?.message ?? '', { ns: 'createVehiclePanel' })}
                help={t('deliveryTimeHelp', { ns: 'createVehiclePanel' })}
              />
            </FormGroup>
          </Form>
        </StackLarge>
      </ShallowPanelBody>
      <CreateVehicleFooter
        onSubmit={() => onCreateVehicle()}
        onClose={() => onClose()}
        disabled={!virtualVehicle}
        loading={createVirtualRequest.isLoading}
      />
    </>
  );
};

export default CreateOrderVehicle;
